/**
 *  @class
 *  @function PredictiveSearch
 *  Enhanced with defensive programming, retry mechanisms, and comprehensive error handling
 */
class PredictiveSearch {
  constructor() {
    try {
      this.container = document.getElementById('Search-Drawer');
      this.initializationAttempts = 0;
      this.maxRetries = 3;

      if (!this.container) {
        this.trackError('container_not_found', 'Search-Drawer element not found during initialization');
        return this.scheduleRetry();
      }

      this.form = this.container.querySelector('form.searchform');
      this.input = this.container.querySelector('input[type="search"]');
      this.defaultTab = this.container.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
      this.scrollableContent = this.container.querySelector('.clerk_wrapper');

      if (!this.predictiveSearchResults) {
        this.useClerkSearch = true;
      } else {
        this.useClerkSearch = false;
      }

      if (!this.form || !this.input) {
        this.trackError('required_elements_missing', 'Form or input elements not found');
        return this.scheduleRetry();
      }

      this.initializeSearchButtons();
      this.setupEventListeners();

      window.predictiveSearchInitialized = true;
      this.trackEvent('search_initialized', { mode: this.useClerkSearch ? 'clerk' : 'predictive' });

    } catch (error) {
      this.trackError('constructor_error', error.message);
      this.scheduleRetry();
    }
  }

  initializeSearchButtons() {
    try {
      this.button = document.querySelectorAll('.thb-quick-search');

      if (!this.button || this.button.length === 0) {
        this.trackError('no_search_buttons', 'No .thb-quick-search elements found');
        return this.scheduleRetry();
      }

      return true;
    } catch (error) {
      this.trackError('button_init_error', error.message);
      return false;
    }
  }

  scheduleRetry() {
    if (this.initializationAttempts < this.maxRetries) {
      this.initializationAttempts++;
      const retryDelay = 300 * this.initializationAttempts;

      setTimeout(() => {
        new PredictiveSearch();
      }, retryDelay);
    } else {
      this.trackError('max_retries_exceeded', `Failed to initialize after ${this.maxRetries} attempts`);
    }
  }

  setupEventListeners() {
    try {
      if (!this.container) {
        return;
      }

      if (!this.form || !this.input) {
        return;
      }

      if (!this.button || this.button.length === 0) {
        return;
      }

      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        try {
          this.onChange(event);
        } catch (error) {
          this.trackError('input_change_error', error.message);
        }
      }, 300).bind(this));

      this.input.addEventListener('focus', () => {
        try {
          if (window.searchScrollLock) {
            window.searchScrollLock.disable();
          }
        } catch (error) {
          // Silent error handling
        }
      });

      this.input.addEventListener('blur', () => {
        try {
          if (!this.container.classList.contains('active') && window.searchScrollLock) {
            window.searchScrollLock.enable();
          }
        } catch (error) {
          // Silent error handling
        }
      });

      this.button.forEach((item, i) => {
        try {
          item.addEventListener('click', (event) => {
            try {
              var _this = this;
              event.preventDefault();

              if (!this.container) {
                return false;
              }

              const body = document.getElementsByTagName("body")[0];
              const html = document.getElementsByTagName("html")[0];

              if (!body || !html) {
                this.trackError('dom_elements_missing', 'Body or HTML element not found');
                return false;
              }

              body.classList.toggle('open-cc');
              html.classList.toggle('open-cc-search');
              this.container.classList.toggle('active');

              const isActive = this.container.classList.contains('active');

              if (isActive) {
                if (window.searchScrollLock) {
                  window.searchScrollLock.disable();
                }

                setTimeout(function () {
                  try {
                    if (_this.input) {
                      _this.input.focus({
                        preventScroll: true
                      });
                    }
                  } catch (error) {
                    // Silent error handling
                  }
                }, 100);

                dispatchCustomEvent('search:open');
                _this.trackEvent('search_opened', { button_index: i });
              } else {
                if (window.searchScrollLock) {
                  window.searchScrollLock.enable();
                }
                _this.trackEvent('search_closed', { button_index: i });
              }

              return false;
            } catch (error) {
              this.trackError('click_handler_error', error.message);
              return false;
            }
          });
        } catch (error) {
          this.trackError('button_listener_error', `Failed to attach listener to button ${i}: ${error.message}`);
        }
      });

    } catch (error) {
      this.trackError('setup_listeners_error', error.message);
    }
  }

  getQuery() {
    try {
      return this.input ? this.input.value.trim() : '';
    } catch (error) {
      return '';
    }
  }

  onChange() {
    try {
      if (this.useClerkSearch) {
        return;
      }

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.classList.remove('active');
        }
        return;
      }

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
        this.getSearchResults(searchTerm);
      }
    } catch (error) {
      this.trackError('onchange_error', error.message);
    }
  }

  onFormSubmit(event) {
    try {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  onFocus() {
    try {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      if (this.predictiveSearchResults) {
        this.getSearchResults(searchTerm);
      }
    } catch (error) {
      // Silent error handling
    }
  }

  getSearchResults(searchTerm) {
    try {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();

      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.classList.add('loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`)
        .then((response) => {
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.classList.remove('loading');
          }
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          this.trackError('search_results_error', error.message);
        });
    } catch (error) {
      this.trackError('get_search_results_error', error.message);
    }
  }

  renderSearchResults(resultsMarkup) {
    try {
      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.innerHTML = resultsMarkup;

      let _this = this,
        submitButton = this.container.querySelector('#search-results-submit');

      if (submitButton) {
        submitButton.addEventListener('click', () => {
          try {
            _this.form.submit();
          } catch (error) {
            // Silent error handling
          }
        });
      }
    } catch (error) {
      this.trackError('render_results_error', error.message);
    }
  }

  close() {
    try {
      if (this.container) {
        this.container.classList.remove('active');
      }

      if (window.searchScrollLock) {
        window.searchScrollLock.enable();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  trackError(errorType, message) {
    try {
      if (typeof gtag !== 'undefined') {
        gtag('event', 'search_error', {
          event_category: 'PredictiveSearch',
          event_label: errorType,
          value: message
        });
      }

      if (typeof analytics !== 'undefined' && analytics.track) {
        analytics.track('Search Error', {
          error_type: errorType,
          error_message: message,
          timestamp: new Date().toISOString()
        });
      }
    } catch (trackingError) {
      // Silent error handling
    }
  }

  trackEvent(eventType, properties = {}) {
    try {
      if (typeof gtag !== 'undefined') {
        gtag('event', eventType, {
          event_category: 'PredictiveSearch',
          ...properties
        });
      }
    } catch (trackingError) {
      // Silent error handling
    }
  }
}

function initializePredictiveSearch(retryCount = 0, context = 'unknown') {
  const maxRetries = 3;

  try {
    if (typeof PredictiveSearch === 'undefined') {
      return false;
    }

    const searchDrawer = document.getElementById('Search-Drawer');
    const searchButtons = document.querySelectorAll('.thb-quick-search');

    if (searchDrawer && searchButtons.length > 0) {
      try {
        new PredictiveSearch();
        return true;
      } catch (error) {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'search_init_error', {
            event_category: 'PredictiveSearch',
            event_label: 'initialization_failed',
            value: error.message
          });
        }

        if (retryCount < maxRetries) {
          const delay = (retryCount + 1) * 500;
          setTimeout(() => initializePredictiveSearch(retryCount + 1, 'error_retry'), delay);
        }
      }
    } else {
      if (retryCount < maxRetries) {
        const delay = (retryCount + 1) * 500;
        setTimeout(() => initializePredictiveSearch(retryCount + 1, 'dom_retry'), delay);
      } else {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'search_init_failed', {
            event_category: 'PredictiveSearch',
            event_label: 'max_retries_exceeded',
            value: `Missing elements after ${maxRetries} retries`
          });
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }

  return false;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializePredictiveSearch(0, 'DOMContentLoaded');
  });
} else {
  initializePredictiveSearch(0, 'immediate');
}

window.addEventListener('load', () => {
  if (!window.predictiveSearchInitialized) {
    initializePredictiveSearch(0, 'window_load');
  }
});

setTimeout(() => {
  if (!window.predictiveSearchInitialized) {
    initializePredictiveSearch(0, 'emergency_fallback');
  }
}, 2000);

setTimeout(() => {
  const clickCapture = document.querySelector('.click-capture');
  if (clickCapture) {
    clickCapture.addEventListener('click', () => {
      try {
        const searchDrawer = document.getElementById('Search-Drawer');
        if (searchDrawer && searchDrawer.classList.contains('active')) {
          if (window.searchScrollLock) {
            window.searchScrollLock.enable();
          }
        }
      } catch (error) {
        // Silent error handling
      }
    });
  }
}, 100);

document.addEventListener('keydown', (event) => {
  try {
    if (event.key === 'Escape') {
      const searchDrawer = document.getElementById('Search-Drawer');
      if (searchDrawer && searchDrawer.classList.contains('active')) {
        if (window.searchScrollLock) {
          window.searchScrollLock.enable();
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }
});
